/** Helpers de conflito de professor na grade (mesmo dia + horário). */

export type OcupacaoProfessorSlot = {
  professorId: string;
  professorNome?: string;
  diaSemana: string;
  numeroAula: number;
  horaInicio?: string | null;
  horaFim?: string | null;
  turmaId: string;
  turmaNome?: string;
  turnoOperacionalId?: string | null;
};

function normTime(t?: string | null): string | null {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

export function timesOverlap(
  aIni?: string | null,
  aFim?: string | null,
  bIni?: string | null,
  bFim?: string | null,
): boolean {
  const A0 = normTime(aIni);
  const A1 = normTime(aFim);
  const B0 = normTime(bIni);
  const B1 = normTime(bFim);
  if (!A0 || !A1 || !B0 || !B1) return false;
  return A0 < B1 && B0 < A1;
}

/** Há conflito real se 2+ slots do mesmo professor se sobrepõem no relógio. */
export function temConflitoHorario(
  slots: Array<{ horaInicio?: string | null; horaFim?: string | null }>,
): boolean {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (timesOverlap(slots[i].horaInicio, slots[i].horaFim, slots[j].horaInicio, slots[j].horaFim)) {
        return true;
      }
    }
  }
  return false;
}

export type SlotExclusao = {
  turmaId: string;
  diaSemana: string;
  numeroAula: number;
};

/**
 * Mescla ocupação da API (outras turmas do MESMO turno) com a grade local,
 * para o filtro refletir rascunho ainda não salvo.
 */
export function mergeOcupacaoComGradeLocal(
  ocupacaoApi: OcupacaoProfessorSlot[],
  gradeLocal: Array<{
    professorId?: string | null;
    diaSemana: string;
    numeroAula: number;
    horaInicio?: string | null;
    horaFim?: string | null;
    tipo?: string | null;
  }>,
  turmaId: string,
  turnoOperacionalId?: string | null,
  turmaNome?: string,
): OcupacaoProfessorSlot[] {
  const turnoId = turnoOperacionalId || null;
  const outros = ocupacaoApi.filter((o) => {
    if (o.turmaId === turmaId) return false;
    // Só outras turmas do mesmo turno (se a API informar o turno)
    if (turnoId && o.turnoOperacionalId && o.turnoOperacionalId !== turnoId) return false;
    return true;
  });
  const locais: OcupacaoProfessorSlot[] = gradeLocal
    .filter((h) => h.professorId && h.tipo !== 'INTERVALO')
    .map((h) => ({
      professorId: h.professorId as string,
      diaSemana: h.diaSemana,
      numeroAula: h.numeroAula,
      horaInicio: h.horaInicio,
      horaFim: h.horaFim,
      turmaId,
      turmaNome,
      turnoOperacionalId: turnoId,
    }));
  return [...outros, ...locais];
}

export function professorOcupadoNoSlot(
  ocupacao: OcupacaoProfessorSlot[],
  opts: {
    professorId: string;
    diaSemana: string;
    numeroAula: number;
    horaInicio?: string | null;
    horaFim?: string | null;
    /** @deprecated use excludeSlot — ignorava a turma inteira e esvaziava o filtro */
    excludeTurmaId?: string;
    /** Ignora só o slot atual (rascunho / edição), não a turma toda */
    excludeSlot?: SlotExclusao;
    /** Turno da turma aberta — conflito só conta no mesmo turno */
    turnoOperacionalId?: string | null;
  },
): OcupacaoProfessorSlot | undefined {
  const turnoId = opts.turnoOperacionalId || null;
  return ocupacao.find((o) => {
    if (o.professorId !== opts.professorId) return false;

    // Conflito só no turno da turma (Manhã ≠ Tarde ≠ Noite)
    if (turnoId && o.turnoOperacionalId && o.turnoOperacionalId !== turnoId) return false;

    if (opts.excludeSlot) {
      if (
        o.turmaId === opts.excludeSlot.turmaId &&
        o.diaSemana === opts.excludeSlot.diaSemana &&
        o.numeroAula === opts.excludeSlot.numeroAula
      ) {
        return false;
      }
    } else if (opts.excludeTurmaId && o.turmaId === opts.excludeTurmaId) {
      return false;
    }

    if (o.diaSemana !== opts.diaSemana) return false;
    if (timesOverlap(opts.horaInicio, opts.horaFim, o.horaInicio, o.horaFim)) return true;
    const aHas = !!(normTime(opts.horaInicio) && normTime(opts.horaFim));
    const bHas = !!(normTime(o.horaInicio) && normTime(o.horaFim));
    if (aHas && bHas) return false;
    return o.numeroAula === opts.numeroAula;
  });
}

/**
 * Verifica se o professor leciona a matéria (seja por p.materias ou por p.turmas).
 */
export function professorLecionaMateria(
  p: {
    materias?: Array<{ id: string } | string> | null;
    turmas?: Array<{ materia?: { id: string }; materiaId?: string }> | null;
  },
  materiaId: string,
): boolean {
  if (p.materias?.some((m: any) => (typeof m === 'string' ? m === materiaId : m?.id === materiaId))) {
    return true;
  }
  if (p.turmas?.some((t: any) => t?.materia?.id === materiaId || t?.materiaId === materiaId)) {
    return true;
  }
  return false;
}

/**
 * Há outro professor (além do já alocado no slot) que leciona a matéria e está livre.
 * A matéria já salva no slot deve ser mantida pelo caller (`m.id === slot.materiaId`).
 */
export function materiaTemProfessorDisponivel(
  materiaId: string,
  professores: Array<{
    id: string;
    ativo?: boolean;
    materias?: Array<{ id: string } | string> | null;
    turmas?: Array<{ materia?: { id: string }; materiaId?: string }> | null;
  }>,
  ocupacao: OcupacaoProfessorSlot[],
  opts: {
    diaSemana: string;
    numeroAula: number;
    horaInicio?: string | null;
    horaFim?: string | null;
    excludeTurmaId?: string;
    excludeSlot?: SlotExclusao;
    turnoOperacionalId?: string | null;
    /** Professor já no slot: não conta como livre para outras matérias */
    slotProfessorId?: string | null;
  },
): boolean {
  return professores.some((p) => {
    if (p.ativo === false) return false;
    if (!professorLecionaMateria(p, materiaId)) return false;
    // Quem já ocupa este horário no slot não “abre” outras matérias
    if (opts.slotProfessorId && p.id === opts.slotProfessorId) return false;
    return !professorOcupadoNoSlot(ocupacao, {
      professorId: p.id,
      diaSemana: opts.diaSemana,
      numeroAula: opts.numeroAula,
      horaInicio: opts.horaInicio,
      horaFim: opts.horaFim,
      excludeTurmaId: opts.excludeTurmaId,
      excludeSlot: opts.excludeSlot,
      turnoOperacionalId: opts.turnoOperacionalId,
    });
  });
}
